import { Autocomplete } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { r, s } from '@/theme/tokens';
import { Box, Text, TextField } from '@/ui/primitives';
import { filterInputSx } from './filterControlStyles';
import type { ReactElement, ReactNode } from 'react';

type SingleSelectOption = { value: string; label: string };

type SingleSelectFilterFieldProps = {
  value: string;
  options: SingleSelectOption[];
  label?: string;
  labelTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  helperText?: ReactNode;
  fontSizePx?: number;
  labelWidthPx?: number;
  labelControlGap?: string;
  inputAriaLabel?: string;
  emptyLabel?: string;
  controlWidthPx?: number;
  fullWidth?: boolean;
  disabled?: boolean;
  inputHeight?: number;
  error?: boolean;
  required?: boolean;
  inputBackgroundColor?: 'background.paper' | 'background.default';
  inputLeftPadding?: string;
  onChange: (next: string) => void;
};

export const SingleSelectFilterField = ({
  value,
  options,
  label,
  labelTransform,
  helperText,
  fontSizePx = 15,
  labelWidthPx,
  labelControlGap = s('sm'),
  inputAriaLabel,
  emptyLabel,
  controlWidthPx = 180,
  fullWidth = false,
  disabled = false,
  inputHeight,
  error = false,
  required = false,
  inputBackgroundColor = 'background.default',
  inputLeftPadding = s('sm'),
  onChange,
}: SingleSelectFilterFieldProps): ReactElement => {
  const controlRef = useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find((option) => option.value === value);
  const resolvedControlWidth = `${controlWidthPx}px`;
  const controlHeight = inputHeight ?? componentSizes.auth.inputHeight;
  const hasLabel = Boolean(label);
  const [measuredControlWidth, setMeasuredControlWidth] = useState(controlWidthPx);

  useEffect(() => {
    const element = controlRef.current;
    if (!element) {
      return;
    }

    const updateWidth = (): void => {
      const nextWidth = element.getBoundingClientRect().width;
      if (nextWidth > 0) {
        setMeasuredControlWidth(nextWidth);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => {
        window.removeEventListener('resize', updateWidth);
      };
    }

    const observer = new ResizeObserver(() => {
      updateWidth();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [controlWidthPx, fullWidth]);

  const dropdownWidth = fullWidth ? `${measuredControlWidth}px` : resolvedControlWidth;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: hasLabel ? labelControlGap : s('none'),
        minWidth: 0,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {hasLabel ? (
        <Text
          role="label"
          color="textSecondary"
          transform={labelTransform}
          sx={{
            whiteSpace: 'nowrap',
            fontSize: `${fontSizePx}px`,
            lineHeight: 1.4,
            fontWeight: (theme) => theme.typography.fontWeightRegular,
          }}
        >
          <Box
            component="span"
            sx={
              labelWidthPx
                ? {
                    display: 'inline-flex',
                    justifyContent: 'flex-end',
                    width: `${labelWidthPx}px`,
                  }
                : undefined
            }
          >
            {label}
          </Box>
        </Text>
      ) : null}
      <Box
        ref={controlRef}
        sx={{
          width: fullWidth ? '100%' : resolvedControlWidth,
          minWidth: fullWidth ? 0 : resolvedControlWidth,
          maxWidth: fullWidth ? 'none' : resolvedControlWidth,
          flex: fullWidth ? '1 1 0' : `0 0 ${resolvedControlWidth}`,
        }}
      >
        <Autocomplete
          disableClearable
          disabled={disabled}
          options={options}
          value={selectedOption}
          isOptionEqualToValue={(option, current) => option.value === current.value}
          getOptionLabel={(option) => option.label}
          onChange={(_event, nextOption) => {
            onChange(nextOption.value);
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box
                component="li"
                key={key}
                {...optionProps}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: s('md'),
                  py: s('sm'),
                  minHeight: `calc(${s('2xl')} + ${s('md')})`,
                }}
              >
                <Text
                  role="bodyMuted"
                  color="textPrimary"
                  component="span"
                  sx={{
                    fontSize: `${fontSizePx}px`,
                    lineHeight: 1.4,
                    fontWeight: (theme) => theme.typography.fontWeightLight,
                  }}
                >
                  {option.label}
                </Text>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              fieldStyle="filter"
              inputHeight={controlHeight}
              placeholder={emptyLabel}
              helperText={helperText}
              error={error}
              required={required}
              // eslint-disable-next-line @typescript-eslint/no-deprecated -- Autocomplete renderInput params supply inputProps with required refs/handlers.
              inputProps={{
                ...params.inputProps,
                'aria-label': inputAriaLabel ?? label ?? 'Select option',
                readOnly: true,
                style: { cursor: 'pointer' },
              }}
            />
          )}
          sx={(theme) => ({
            ...(typeof filterInputSx === 'function' ? filterInputSx(theme) : {}),
            width: '100%',
            minWidth: 0,
            '& .MuiAutocomplete-inputRoot': {
              minHeight: `${controlHeight}px !important`,
              height: `${controlHeight}px !important`,
              maxHeight: `${controlHeight}px !important`,
              backgroundColor: inputBackgroundColor,
              alignItems: 'center',
              paddingTop: `${s('none')} !important`,
              paddingBottom: `${s('none')} !important`,
              minWidth: 0,
              cursor: 'pointer',
            },
            '& .MuiAutocomplete-input': {
              minHeight: `${controlHeight}px !important`,
              height: `${controlHeight}px !important`,
              paddingTop: `${s('none')} !important`,
              paddingBottom: `${s('none')} !important`,
              paddingLeft: `${inputLeftPadding} !important`,
              marginLeft: `${s('none')} !important`,
              lineHeight: '1 !important',
              cursor: 'pointer',
            },
            '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiAutocomplete-inputRoot': {
              ...theme.typography.body1,
              fontSize: `${fontSizePx}px`,
              cursor: 'pointer',
              color: theme.palette.common.white,
            },
            '& .MuiInputBase-input, & .MuiAutocomplete-input, & input': {
              ...theme.typography.body1,
              fontSize: `${fontSizePx}px`,
              cursor: 'pointer',
              color: theme.palette.common.white,
            },
            '& .MuiInputBase-input::placeholder, & .MuiAutocomplete-input::placeholder, & input::placeholder':
              {
                ...theme.typography.body1,
                fontSize: `${fontSizePx}px`,
                color: theme.palette.common.white,
                opacity: 1,
              },
            '& .Mui-disabled': {
              opacity: 1,
            },
            '& .MuiInputBase-input.Mui-disabled, & .MuiAutocomplete-input.Mui-disabled, & input:disabled':
              {
                WebkitTextFillColor: theme.palette.text.disabled,
                color: theme.palette.text.disabled,
              },
            '& .MuiSvgIcon-root.Mui-disabled, & .MuiAutocomplete-popupIndicator.Mui-disabled': {
              color: theme.palette.text.disabled,
            },
            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
          })}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 0],
                  },
                },
              ],
              sx: {
                '& .MuiAutocomplete-paper': {
                  minWidth: dropdownWidth,
                  width: fullWidth ? dropdownWidth : 'max-content',
                  maxWidth: 'min(560px, calc(100vw - 32px))',
                },
              },
            },
            paper: {
              sx: {
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: `${r('md')} !important`,
                boxShadow: 'none',
                backgroundImage: 'none',
                overflow: 'hidden !important',
                clipPath: `inset(0 round ${r('md')})`,
                '--Paper-overlay': 'none',
                '--Paper-shadow': 'none',
                minWidth: dropdownWidth,
                width: fullWidth ? dropdownWidth : 'max-content',
                maxWidth: 'min(560px, calc(100vw - 32px))',
              },
            },
            listbox: {
              sx: {
                p: s('none'),
                m: s('none'),
                minWidth: dropdownWidth,
                overflow: 'hidden',
                '& .MuiAutocomplete-option': {
                  paddingLeft: `${s('md')} !important`,
                  paddingRight: `${s('md')} !important`,
                  whiteSpace: 'nowrap',
                },
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export type { SingleSelectFilterFieldProps, SingleSelectOption };
